/**
 * Product Promo Modal
 * Opens a popup with collection products in the theme's native slideshow
 * when a promo badge is clicked in the cart drawer.
 *
 * The section "promo-popup-content" is fetched via the Shopify Sections API
 * and returns a fully-formed <slideshow-component> which self-initialises
 * as soon as it is inserted into the DOM (connectedCallback).
 */
class ProductPromoModal {
  constructor() {
    this.swat = null;
    this.#init();
    this.#initSwym();
  }

  getElements() {
    const modal = document.getElementById('product-promo-modal');
    if (!modal) return null;

    return {
      modal,
      titleEl: modal.querySelector('.product-promo-modal__title'),
      shopDealEl: modal.querySelector('.product-promo-modal__shop-deal'),
      contentEl: modal.querySelector('.product-promo-modal__content'),
      closeBtn: modal.querySelector('.product-promo-modal__close'),
      overlayEl: modal.querySelector('.product-promo-modal__overlay')
    };
  }

  /* ─── Private helpers ─────────────────────────────────────── */

  #initSwym() {
    window.SwymCallbacks = window.SwymCallbacks || [];
    window.SwymCallbacks.push((swat) => {
      this.swat = swat;
    });
  }

  #init() {
    // Delegate click on promo badge triggers (works for dynamically rendered carts)
    document.addEventListener('click', (e) => {
      const trigger = e.target?.closest('.js-promo-badge-trigger');
      if (trigger) {
        // Only intercept and open the popup in Cart or YMAL contexts
        const isCartOrYmal = !!(
          trigger.closest('.cart-drawer') ||
          trigger.closest('.cart-drawer-wrapper') ||
          trigger.closest('.cart-items') ||
          trigger.closest('.you-may-also-like') ||
          trigger.closest('.you-may-also-like__list')
        );

        if (!isCartOrYmal) {
          // Allow default navigation to the collection URL
          return;
        }

        e.preventDefault();
        const handle = trigger.dataset.promoHandle;
        const title = trigger.dataset.promoTitle || (trigger.querySelector('span') ?? trigger).textContent?.trim();
        const link = trigger.getAttribute('href') || trigger.href;

        if (handle && title && link) {
          this.open(handle, title, link);
        } else {
          console.warn('[ProductPromoModal] Trigger element missing expected data attributes:', trigger);
        }
        return;
      }

      // Delegate click on close button
      const closeBtn = e.target?.closest('.product-promo-modal__close');
      if (closeBtn) {
        this.close();
        return;
      }

      // Delegate click on overlay
      const overlay = e.target?.closest('.product-promo-modal__overlay');
      if (overlay) {
        this.close();
        return;
      }
    });

    // Delegate click on quick add / choose buttons inside the promo popup
    document.addEventListener('click', (e) => {
      const quickAddBtn = e.target?.closest(
        '.product-promo-modal__content .quick-add__button, .product-promo-modal__content .add-to-cart-button, .product-promo-modal__content quick-add-component, .product-promo-modal__content .quick-add'
      );
      if (!quickAddBtn) return;

      if (window.innerWidth <= 749) {
        window.__openedFromPromoModal = true;
      }

      // If the card has a native quick-add-component, allow its own handleClick to handle the click
      const quickAddComponent = quickAddBtn.closest('quick-add-component');
      if (quickAddComponent) {
        return;
      }

      e.preventDefault();
      e.stopPropagation();

      const card = quickAddBtn.closest('product-card') || quickAddBtn.closest('.product-card') || quickAddBtn.closest('.promo-popup-slide');
      let productUrl = '';
      if (card) {
        if (typeof card.productPageUrl === 'string' && card.productPageUrl) {
          productUrl = card.productPageUrl;
        } else {
          const inputLink = card.querySelector('input.product-card__link');
          if (inputLink && inputLink.value) {
            productUrl = inputLink.value;
          } else {
            const anchorLink = card.querySelector('a.promo-popup-card__title-link') || card.querySelector('a[href*="/products/"]');
            if (anchorLink && anchorLink.href) {
              productUrl = anchorLink.href;
            }
          }
        }

        if (productUrl && !productUrl.includes('variant=')) {
          const variantInput = card.querySelector('input[type="radio"]:checked[data-variant-id]') ||
                               card.querySelector('input[name="id"][ref="variantId"]') ||
                               card.querySelector('input[name="id"]');
          const variantId = variantInput?.getAttribute('data-variant-id') ||
                            variantInput?.dataset?.variantId ||
                            variantInput?.value;
          if (variantId && /^\d+$/.test(variantId.trim())) {
            try {
              const url = new URL(productUrl, window.location.origin);
              url.searchParams.set('variant', variantId.trim());
              productUrl = url.toString();
            } catch (_) {}
          }
        }
      }

      if (productUrl && window.quickAdd && typeof window.quickAdd.open === 'function') {
        window.quickAdd.open(productUrl);
      }
    }, { capture: true });

    // Delegate click on wishlist buttons inside the popup
    document.addEventListener('click', (e) => {
      const wishlistBtn = e.target?.closest('.product-promo-modal__content .button-collection-wishlist');
      if (wishlistBtn) {
        if (!window.__isLoggedIn) {
          e.preventDefault();
          e.stopPropagation();

          if (typeof window.openAccountDrawer === 'function') {
            window.openAccountDrawer();
          } else {
            const drawer = document.querySelector('account-drawer-component');
            if (drawer && typeof drawer.open === 'function') {
              drawer.open();
            } else {
              const wrapper = document.querySelector('.account-drawer-wrapper');
              if (wrapper) {
                wrapper.classList.add('is-open');
                document.body.classList.add('open_account');
              }
            }
          }

          if (window.productPromoModal && typeof window.productPromoModal.close === 'function') {
            window.productPromoModal.close();
          }
          const cartDrawer = document.querySelector('cart-drawer-component');
          if (cartDrawer && typeof cartDrawer.closeDialog === 'function') {
            cartDrawer.closeDialog();
          }
          return;
        }
      }
    }, { capture: true });

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        const elements = this.getElements();
        if (elements && elements.modal && elements.modal.style.display === 'flex') {
          this.close();
        }
      }
    });
  }

  /* ─── Public API ──────────────────────────────────────────── */

  async open(handle, title, link) {
    const elements = this.getElements();
    if (!elements) return;

    const { modal, titleEl, shopDealEl, contentEl } = elements;
    if (!handle || !titleEl || !shopDealEl || !contentEl || !modal) return;

    // Update header
    titleEl.textContent = title;
    shopDealEl.setAttribute('href', link);

    // Show modal with loading state
    contentEl.innerHTML = '<div style="width:100%;text-align:center;padding:40px 0;">Loading&hellip;</div>';
    modal.style.display = 'flex';
    document.body.style.overflow = 'hidden';
    document.body.classList.add('product-promo-modal-open');

    try {
      const root = window.Shopify?.routes?.root ?? '/';
      const response = await fetch(`${root}collections/${handle}?sections=promo-popup-content`);
      const json = await response.json();
      const sectionHtml = json['promo-popup-content'];

      // Re-query elements after dynamic fetch in case they changed
      const currentElements = this.getElements();
      if (!currentElements || !currentElements.contentEl) return;

      if (sectionHtml?.trim()) {
        // Strip the outer shopify-section wrapper so only the slideshow markup
        // lands inside product-promo-modal__content
        const temp = document.createElement('div');
        temp.innerHTML = sectionHtml;
        const inner = temp.querySelector('.shopify-section');
        currentElements.contentEl.innerHTML = inner ? inner.innerHTML : sectionHtml;

        // Re-trigger the Wishlist IO app's own initialization so it rebinds
        // all .button-collection-wishlist elements injected into the popup.
        // Using a short delay ensures the new DOM nodes are fully connected.
        setTimeout(() => {
          document.body.dispatchEvent(new CustomEvent('initTheWishlist', { bubbles: true }));
        }, 100);

        // The theme's <slideshow-component> custom element calls connectedCallback
        // automatically the moment it is attached to the live DOM, so no manual
        // initialisation is needed here.
      } else {
        currentElements.contentEl.innerHTML =
          '<div style="width:100%;text-align:center;padding:40px 0;">No products found in this promotion.</div>';
      }
    } catch (err) {
      console.error('[ProductPromoModal] fetch error:', err);
      const currentElements = this.getElements();
      if (currentElements && currentElements.contentEl) {
        currentElements.contentEl.innerHTML =
          '<div style="width:100%;text-align:center;padding:40px 0;">Error loading products.</div>';
      }
    }
  }

  close() {
    const elements = this.getElements();
    if (!elements) return;

    const { modal, contentEl } = elements;
    if (!modal) return;
    modal.style.display = 'none';
    document.body.style.overflow = '';
    document.body.classList.remove('product-promo-modal-open');

    // Clear content so next open() always fetches fresh
    if (contentEl) contentEl.innerHTML = '';
  }
}

// Initialise once
if (!window.productPromoModalInitialized) {
  window.productPromoModal = new ProductPromoModal();
  window.productPromoModalInitialized = true;
}
